import { Box, Stack } from "@mui/material";
import React, { useState } from "react";

import { useLabels } from "api/services/useLabels";
import { ImageCanvas } from "components/common/ImageCanvas";
import { DEFAULT_DISAGREEMENT_CHECK_RESPONSE, useReviewPage } from "context/reviewPageContext/reviewPageContext";
import { CustomModal } from "new-components/modals/CustomModal";
import { TextFieldInput } from "new-components/textFieldInput/TextFieldInput";
import { Typography } from "new-components/typography/Typography";
import { Disagreement } from "types/disagreements";
import { DATA_TYPE, Posicheck, REVIEW_REASON_CHOICES } from "types/imageQueries";

import { ButtonPressedEnum, ButtonPressedProps } from "../types";

const NOTE_CHAR_LIMIT = 1000;

const ADD_NOTE_MESSAGES = {
  [ButtonPressedEnum.KEEP]: "Please describe why you chose to keep your answer:",
  [ButtonPressedEnum.CHANGE]: "Please describe why you chose to change your answer:",
};

interface DisagreementNoteModalProps {
  buttonPressed: ButtonPressedProps;
  imageUrl: string;
  iqId: string;
  openModal: boolean;
  onClose: () => void;
  changedAnswer?: string;
  feedbackText?: string;
}

function getDisagreementReviewReasonForPosicheck(posicheck: Posicheck): REVIEW_REASON_CHOICES | null {
  switch (posicheck.review_reason) {
    case REVIEW_REASON_CHOICES.ESCALATION:
      return REVIEW_REASON_CHOICES.DISAGREEMENT;

    case REVIEW_REASON_CHOICES.ASSESSMENT:
      return REVIEW_REASON_CHOICES.DISAGREEMENT_ON_ASSESSMENT;

    default:
      return null;
  }
}

export function DisagreementNoteModal(props: DisagreementNoteModalProps) {
  const { buttonPressed, imageUrl, iqId, openModal, onClose, changedAnswer, feedbackText } = props;

  const {
    currentImageQuery,
    disagreementCheckResponse: { challengedAnswer, disagreementId },
    setDisagreementCheckResponse,
    removeRecentIQ,
    startPooling,
  } = useReviewPage();

  const [note, setNote] = useState<string>("");
  const [isNoteTouched, setIsNoteTouched] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { createLabelAsync } = useLabels();

  const hasNoteExceededLimit = note.length > NOTE_CHAR_LIMIT;
  const isNoteError = (isNoteTouched && note.length === 0) || hasNoteExceededLimit;

  const handleCancel = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const handleConfirmation = async () => {
    if (!currentImageQuery) {
      return;
    }

    const feedbackTextToSend = feedbackText ? `${feedbackText}; ${note}` : note;

    try {
      setIsLoading(true);

      if (currentImageQuery.data_type === DATA_TYPE.DISAGREEMENT) {
        const disagreement = currentImageQuery as Disagreement;

        await createLabelAsync({
          label: changedAnswer ?? challengedAnswer ?? "",
          posicheck_id: disagreement.iq_id,
          review_reason: REVIEW_REASON_CHOICES.DISAGREEMENT,
          feedback_text: feedbackTextToSend,
        });
      } else {
        const posicheck = currentImageQuery as Posicheck;

        await createLabelAsync({
          label: changedAnswer ?? challengedAnswer ?? "",
          posicheck_id: posicheck.id,
          review_reason: getDisagreementReviewReasonForPosicheck(posicheck) ?? REVIEW_REASON_CHOICES.DISAGREEMENT,
          feedback_text: feedbackTextToSend,
          review_bbox_info: currentImageQuery.review_bbox_info,
        });
      }

      const newPool = removeRecentIQ(currentImageQuery?.id || disagreementId || "");
      if (newPool.length === 0) startPooling();
      setDisagreementCheckResponse(DEFAULT_DISAGREEMENT_CHECK_RESPONSE);
      setIsLoading(false);
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CustomModal
      open={openModal}
      onClose={onClose}
      size="medium"
      title="Add a note"
      confirmationButtonTitle="Save"
      cancelButtonTitle="Cancel"
      isConfirmationDisabled={isLoading || !isNoteTouched || isNoteError}
      isCancelDisabled={isLoading}
      onConfirmation={handleConfirmation}
      onCancel={handleCancel}
      body={
        <Stack sx={{ width: "100%", gap: "20px", padding: "20px 50px" }}>
          <Typography variant="subtitle" fontWeight="semibold" textAlign="center">
            {ADD_NOTE_MESSAGES[buttonPressed ?? ButtonPressedEnum.KEEP]}
          </Typography>
          <Box sx={{ display: "flex", gap: "20px" }}>
            <Stack sx={{ width: "230px", height: "115px" }}>
              <ImageCanvas imageUrl={imageUrl} iqId={iqId} style={{ width: "100%", height: "100%" }} />
            </Stack>
            <Box sx={{ flexGrow: 1 }}>
              <TextFieldInput
                variant="standard"
                placeholder="Add note here"
                rows={4}
                error={isNoteError}
                helperText={isNoteTouched && note.length === 0 ? "Note is required" : ""}
                characterLimit={NOTE_CHAR_LIMIT}
                value={note}
                onChange={(e) => {
                  const { value } = e.target;
                  setNote(value);
                  setIsNoteTouched(true);
                }}
              />
            </Box>
          </Box>
        </Stack>
      }
    />
  );
}
